import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { paths } from '@/app/router/paths';
import { FIELD_ROLES } from '@/app/router/roles';
import { Button, Card, cn, Drawer, layout, opnameGridColumns } from '@/design-system';
import { photoMutations } from '@/entities/photo';
import { hasRole, useSessionStore } from '@/shared/api';
import { formatDayTimeShort, useBreakpoint } from '@/shared/lib';
import { syncLabel, useOnlineStatus, useSyncState } from '@/shared/offline';
import { ErrorState } from '@/shared/ui';

import { CompletionPanel } from './components/CompletionPanel';
import { ConflictNotice } from './components/ConflictNotice';
import { FindingsPanel } from './components/FindingsPanel';
import { SurveyErrorBoundary } from './components/SurveyErrorBoundary';
import { SurveyHeader } from './components/SurveyHeader';
import { SurveyMenu } from './components/SurveyMenu';
import { SurveyStream } from './components/SurveyStream';
import { SurveyTree } from './components/SurveyTree';
import { surveyCopy, surveyErrors } from './copy';
import { useBackTarget } from './hooks/useBackTarget';
import { useOpenSharedQuestions } from './hooks/useOpenSharedQuestions';
import { usePartSwipe } from './hooks/usePartSwipe';
import { useIgnoreStrayFileDrops } from './hooks/usePhotoDrop';
import {
    useSurveyData,
    useSurveyState,
    type SurveyData,
    type SurveyLoad,
} from './hooks/useSurveyState';
import type { NodeId } from './engine';
import { photoQueue } from './model/photo-queue';
import { shownStream, streamCount } from './model/stream';

type OpenPanel = 'tree' | 'findings' | 'menu' | null;

/** De panelen van de opname, zodat de knoppen in de balk zeggen wat ze openen. */
const PANEL_IDS = {
    tree: 'opname-onderwerpen',
    findings: 'opname-bevindingen',
    menu: 'opname-menu',
} as const;

function useIsFieldRole(): boolean {
    return useSessionStore((state) => hasRole(state.session, FIELD_ROLES));
}

/**
 * Scherm 6 - opname NTA 8060. Het kernscherm: de inspecteur werkt hier 60 tot
 * 120 minuten per object, op een tablet, offline. Alles wat hier een tap
 * bespaart is marge (principe 1).
 *
 * Zichtbaarheid, tellers, sweep en kosten komen uit `./engine`; dit scherm
 * rekent zelf niets uit.
 */
export function SurveyPage() {
    const { inspectionId } = useParams<{ inspectionId: string }>();

    if (!inspectionId) {
        return <ErrorState error={new Error('Deze keuring bestaat niet.')} />;
    }

    return <SurveyScreen inspectionId={inspectionId} />;
}

function SurveyScreen({ inspectionId }: { inspectionId: string }) {
    const load = useSurveyData(inspectionId);

    if (load.status !== 'ready') {
        return <SurveyStatus load={load} />;
    }

    return <ReadySurvey data={load.data} />;
}

/**
 * Zolang de opname niet te openen is, zegt het scherm waarom - neutraal, want
 * geen van deze toestanden is een fout van de inspecteur.
 */
function SurveyStatus({ load }: { load: Exclude<SurveyLoad, { status: 'ready' }> }) {
    const navigate = useNavigate();
    const back = useBackTarget(null);
    const copy = surveyCopy.load;
    const content = {
        loading: { title: copy.loading, body: null },
        'no-environment': { title: copy.noEnvironment, body: copy.noEnvironmentBody },
        impersonating: { title: copy.impersonating, body: copy.impersonatingBody },
        'not-found': { title: copy.notFound, body: copy.notFoundBody },
        preparing: { title: copy.preparing, body: copy.preparingBody },
        'no-questionnaire': { title: copy.noQuestionnaire, body: copy.noQuestionnaireBody },
        'package-pending': { title: copy.packagePending, body: copy.packagePendingBody },
        'package-invalid': { title: copy.packageInvalid, body: copy.packageInvalidBody },
    }[load.status];

    return (
        <div className="flex h-full min-h-0 flex-col items-start gap-3 overflow-auto bg-[var(--surface-page)] px-[22px] pt-8 pb-10">
            <h1 className="m-0 font-[family-name:var(--font-display)] text-[22px] leading-[1.25] font-semibold text-[var(--text-primary)]">
                {content.title}
            </h1>
            {content.body ? (
                <p className="m-0 max-w-[520px] text-[14px] leading-[1.5] text-[var(--text-secondary)]">
                    {content.body}
                </p>
            ) : null}
            {load.status === 'loading' ? null : (
                <Button variant="outline" size="md" onClick={() => void navigate(back.to)}>
                    {back.label}
                </Button>
            )}
        </div>
    );
}

function ReadySurvey({ data }: { data: SurveyData }) {
    const navigate = useNavigate();
    const online = useOnlineStatus();
    const { state: sync, flushNow } = useSyncState();
    const { level } = useBreakpoint();
    const survey = useSurveyState(data);
    useIgnoreStrayFileDrops();
    const orderUuid = data.inspection.orderUuid;
    const back = useBackTarget(orderUuid);
    const field = useIsFieldRole();
    const readOnly = survey.readOnly;

    const [panel, setPanel] = useState<OpenPanel>(null);
    const [completing, setCompleting] = useState(false);

    const queuedPhotos = useMemo(
        () => photoQueue(data.photos, data.answers, data.manualFindings),
        [data.photos, data.answers, data.manualFindings],
    );
    /** Eén foto opnieuw, en dan meteen een ronde - niet wachten op de backoff. */
    const retryPhoto = useCallback(
        async (clientUuid: string) => {
            try {
                await photoMutations.retry(clientUuid, data.db);
                await flushNow();
            } catch (cause) {
                console.error('[opname] foto opnieuw aanbieden mislukt', cause);
            }
        },
        [data.db, flushNow],
    );
    const skipPhoto = useCallback(
        async (clientUuid: string) => {
            try {
                await photoMutations.skipUpload(clientUuid, data.db);
                await flushNow();
            } catch (cause) {
                console.error('[opname] foto overslaan mislukt', cause);
            }
        },
        [data.db, flushNow],
    );

    /** De panelen sluiten elkaar uit; navigeren sluit ze allemaal. */
    const openPanel = useCallback((next: OpenPanel) => {
        setPanel((current) => (current === next ? null : next));
    }, []);

    const closePanels = useCallback(() => setPanel(null), []);

    const {
        snapshot,
        object,
        notes,
        rows,
        stream,
        meta,
        position,
        findings,
        questionFindings,
        costs,
        photosByQuestion,
        activeNodeId,
        activeRuntime,
        mainNodeId,
        mainRuntime,
        sweepCount,
        hiddenQuestions,
    } = survey;
    const questions = stream.flatMap((entry) => (entry.kind === 'question' ? [entry.view] : []));
    const shared = useOpenSharedQuestions(mainNodeId, questions);
    /*
     * Wat er werkelijk in beeld staat: een vraag die elders hoort en daar al beantwoord
     * is, komt hier niet nog eens, en een tussenkop waaronder dan niets meer staat ook
     * niet. De teller boven de stroom telt precies dat.
     */
    const shown = shownStream(stream, (view) => !view.askedIn || shared.isOpen(view.key));
    const shownCount = streamCount(shown);
    const column = useRef<HTMLElement>(null);

    /*
     * Een onderdeel uit de boom kiezen scrolt naar zijn tussenkop in de stroom;
     * het hoofdonderdeel zelf begint bovenaan. Zo blijft het één scherm per
     * hoofdonderdeel, ook als je halverwege ergens naartoe springt.
     */
    useEffect(() => {
        const element = column.current;

        if (!element) {
            return;
        }

        const target =
            activeNodeId && activeNodeId !== mainNodeId
                ? element.querySelector<HTMLElement>(`[data-node="${activeNodeId}"]`)
                : null;

        if (target && typeof target.scrollIntoView === 'function') {
            target.scrollIntoView({ block: 'start' });

            return;
        }

        element.scrollTop = 0;
    }, [activeNodeId, mainNodeId]);

    const selectNode = (nodeId: NodeId) => {
        survey.selectNode(nodeId);
        setCompleting(false);
        closePanels();
    };

    const goToNeighbour = (direction: -1 | 1) => {
        survey.goToNeighbour(direction);
        closePanels();
    };

    const swipe = usePartSwipe(
        () => goToNeighbour(-1),
        () => goToNeighbour(1),
    );

    const goToFirstIncomplete = () => {
        survey.goToFirstIncomplete();
        setCompleting(false);
        closePanels();
    };

    const primaryAction = !readOnly
        ? {
              label: surveyCopy.header.complete,
              onClick: () => {
                  setCompleting(true);
                  closePanels();
              },
          }
        : !field && orderUuid
          ? {
                label: surveyCopy.locked.toReport,
                onClick: () => void navigate(paths.report(orderUuid)),
            }
          : null;

    const tree = <SurveyTree rows={rows} currentNodeId={activeNodeId} onSelect={selectNode} />;

    const findingsPanel = (
        <FindingsPanel
            findings={findings}
            costs={costs}
            answered={snapshot.progress.answeredQuestions}
            visible={snapshot.progress.visibleQuestions}
            hidden={hiddenQuestions}
            {...(readOnly
                ? {}
                : {
                      manual: {
                          places: survey.places,
                          defaultPlace: activeRuntime?.id ?? survey.places[0]?.value ?? '',
                          onSave: (findingUuid, draft) =>
                              void survey.saveManualFinding(findingUuid, draft),
                          onRemove: (findingUuid) => void survey.removeManualFinding(findingUuid),
                          newFindingUuid: () => survey.newManualFindingUuid(),
                          photosByFinding: survey.findingPhotos,
                          onAddPhotos: (findingUuid, files) =>
                              void survey.addFindingPhotos(findingUuid, files),
                          onRemovePhoto: (clientUuid) => void survey.removePhoto(clientUuid),
                          onDiscardPhotos: (findingUuid) =>
                              void survey.discardFindingPhotos(findingUuid),
                      },
                  })}
        />
    );

    return (
        /*
         * Het scherm vult de volle hoogte van het werkvlak en bepaalt zelf zijn
         * scrollgebieden: de kolommen scrollen, de pagina nooit. Zo blijven de
         * kop, de teller en de voortgangsbalk altijd in beeld.
         */
        <div className="flex h-full min-h-0 flex-col overflow-hidden bg-[var(--surface-page)]">
            <SurveyHeader
                eyebrow={surveyCopy.header.partPosition(position.index, position.total)}
                title={mainRuntime?.displayName ?? ''}
                answered={snapshot.progress.answeredQuestions}
                visible={snapshot.progress.visibleQuestions}
                percentage={snapshot.progress.percentage}
                online={online}
                syncText={syncLabel(sync)}
                onSyncNow={
                    /* Dezelfde knop als bij Afronden, onder dezelfde voorwaarde. */
                    data.pending.operations + data.pending.photos > 0
                        ? () => void flushNow()
                        : undefined
                }
                narrow={level === 'narrow'}
                onNextIncomplete={
                    snapshot.progress.firstIncompleteNodeId ? goToFirstIncomplete : null
                }
                tree={
                    level === 'narrow'
                        ? {
                              open: panel === 'tree',
                              onToggle: () => openPanel('tree'),
                              controls: PANEL_IDS.tree,
                          }
                        : null
                }
                findings={
                    level === 'wide'
                        ? null
                        : {
                              open: panel === 'findings',
                              onToggle: () => openPanel('findings'),
                              controls: PANEL_IDS.findings,
                          }
                }
                menu={{
                    open: panel === 'menu',
                    onToggle: () => openPanel('menu'),
                    controls: PANEL_IDS.menu,
                }}
            />

            <div
                className="relative grid min-h-0 flex-1"
                style={{ gridTemplateColumns: opnameGridColumns[level] }}
            >
                {level === 'narrow' ? null : tree}

                <SurveyErrorBoundary key={mainNodeId ?? 'leeg'}>
                    <section
                        ref={column}
                        aria-label={surveyCopy.item.column}
                        {...swipe}
                        className={cn(
                            'overflow-auto bg-[var(--surface-page)] pt-1 pb-8',
                            level === 'narrow' ? 'px-3' : 'px-4',
                        )}
                    >
                        <ConflictNotice
                            conflicts={survey.conflicts}
                            graph={survey.graph}
                            onAcknowledge={(ids) => void survey.dismissConflicts(ids)}
                        />

                        {survey.writeError ? (
                            <WriteErrorNotice
                                message={survey.writeError}
                                onDismiss={survey.dismissWriteError}
                            />
                        ) : null}

                        {completing ? (
                            <CompletionPanel
                                report={survey.completion}
                                status={data.inspection.status}
                                completedAt={data.inspection.completedAt}
                                submittedAt={data.inspection.submittedAt ?? null}
                                online={online}
                                pending={data.pending}
                                photoQueue={queuedPhotos}
                                requiredPhotosOnTheWay={survey.requiredPhotosOnTheWay}
                                busy={survey.submission.busy}
                                error={survey.submission.error}
                                onOpenNode={selectNode}
                                onComplete={() => void survey.completeInspection()}
                                onSubmit={() => void survey.submitInspection()}
                                onSyncNow={() => void flushNow()}
                                onRetryPhoto={(clientUuid) => void retryPhoto(clientUuid)}
                                onSkipPhoto={(clientUuid) => void skipPhoto(clientUuid)}
                                onClose={() => setCompleting(false)}
                            />
                        ) : (
                            <>
                                {readOnly ? (
                                    <LockedNotice
                                        submittedAt={data.inspection.submittedAt ?? null}
                                    />
                                ) : null}

                                {/*
                                 * Waar je bent staat in de balk. Deze regel blijft bovenaan
                                 * plakken: zo is "Geen gebreken hier" ook halverwege een lang
                                 * onderdeel één tik weg, en kost hij zijn hoogte maar één keer.
                                 */}
                                <div
                                    className={cn(
                                        'sticky top-0 z-10 flex min-h-11 flex-wrap items-center',
                                        'justify-between gap-x-3 gap-y-1 border-b border-[var(--border-subtle)]',
                                        'bg-[var(--surface-page)]',
                                        level === 'narrow' ? '-mx-3 px-3' : '-mx-4 px-4',
                                    )}
                                >
                                    <p className="m-0 text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                                        {surveyCopy.item.meta(
                                            shownCount.answered,
                                            shownCount.applicable,
                                            meta.skipped,
                                        )}
                                    </p>

                                    {sweepCount > 0 && !readOnly ? (
                                        <Button
                                            variant="accentSoft"
                                            size="md"
                                            onClick={() => void survey.sweepCurrentNode()}
                                        >
                                            {surveyCopy.item.sweep(sweepCount)}
                                        </Button>
                                    ) : null}
                                </div>

                                <SurveyStream
                                    entries={shown}
                                    photosByQuestion={photosByQuestion}
                                    notes={notes}
                                    questionFindings={questionFindings}
                                    buildYear={survey.buildYear}
                                    shared={shared}
                                    readOnly={readOnly}
                                    onToggle={(view, optionId, selected) =>
                                        void survey.toggleOption(view, optionId, selected)
                                    }
                                    onNoteChange={(key, text) =>
                                        void survey.setReportText(key, text)
                                    }
                                    onLocationChange={(key, text) =>
                                        void survey.setFindingLocation(key, text)
                                    }
                                    onAmountChange={(key, cents, prompted) =>
                                        void survey.setFindingAmount(key, cents, prompted)
                                    }
                                    onUrgencyChange={(key, urgency) =>
                                        void survey.setFindingUrgency(key, urgency)
                                    }
                                    onNhgChange={(key, nhg) => void survey.setFindingNhg(key, nhg)}
                                    onValueCommit={(view, raw) => void survey.setValue(view, raw)}
                                    onAddPhotos={(view, files) =>
                                        void survey.addPhotos(view, files)
                                    }
                                    onRemovePhoto={(clientUuid) =>
                                        void survey.removePhoto(clientUuid)
                                    }
                                    onMultiCountChange={(nodeId, count) =>
                                        void survey.setMultiCount(nodeId, count)
                                    }
                                />

                                <div className="mt-3 flex flex-wrap justify-between gap-3">
                                    <Button
                                        variant="outline"
                                        size="lg"
                                        onClick={() => goToNeighbour(-1)}
                                    >
                                        {surveyCopy.item.previous}
                                    </Button>
                                    <Button
                                        variant="dark"
                                        size="lg"
                                        onClick={() => goToNeighbour(1)}
                                    >
                                        {surveyCopy.item.next}
                                    </Button>
                                </div>
                            </>
                        )}
                    </section>
                </SurveyErrorBoundary>

                {level === 'wide' ? findingsPanel : null}

                {level === 'narrow' ? (
                    <Drawer
                        id={PANEL_IDS.tree}
                        open={panel === 'tree'}
                        onClose={closePanels}
                        side="left"
                        width={layout.opnameTreeDrawer}
                        position="absolute"
                        title={surveyCopy.panels.topics}
                    >
                        {tree}
                    </Drawer>
                ) : null}

                {level === 'wide' ? null : (
                    <Drawer
                        id={PANEL_IDS.findings}
                        open={panel === 'findings'}
                        onClose={closePanels}
                        side="right"
                        width={layout.opnameFindDrawer}
                        position="absolute"
                        title={surveyCopy.panels.findings}
                    >
                        {findingsPanel}
                    </Drawer>
                )}

                <Drawer
                    id={PANEL_IDS.menu}
                    open={panel === 'menu'}
                    onClose={closePanels}
                    side="right"
                    width={layout.opnameFindDrawer}
                    position="absolute"
                    title={surveyCopy.menu.title}
                >
                    <SurveyMenu
                        eyebrow={object.eyebrow}
                        address={object.address}
                        answered={snapshot.progress.answeredQuestions}
                        visible={snapshot.progress.visibleQuestions}
                        percentage={snapshot.progress.percentage}
                        back={back}
                        onBack={() => void navigate(back.to)}
                        onNextIncomplete={
                            snapshot.progress.firstIncompleteNodeId ? goToFirstIncomplete : null
                        }
                        primaryAction={primaryAction}
                        onClose={closePanels}
                    />
                </Drawer>
            </div>
        </div>
    );
}

/**
 * Na indienen is de opname bevroren. De melding is neutraal: indienen is de
 * bedoeling, geen fout.
 */
function LockedNotice({ submittedAt }: { submittedAt: string | null }) {
    return (
        <Card variant="muted" padding="md" role="status" className="mb-[14px]">
            <p className="m-0 text-[14px] leading-[1.4] font-medium text-[var(--text-primary)]">
                {surveyCopy.locked.title(submittedAt ? formatDayTimeShort(submittedAt) : null)}
            </p>
            <p className="m-0 mt-1 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                {surveyCopy.locked.body}
            </p>
        </Card>
    );
}

/**
 * Een mislukte lokale schrijfactie is geen reden om de opname te stoppen: de
 * melding staat inline boven de vragen, de al ingevulde antwoorden blijven
 * staan en er is geen blokkerende dialoog (principe 4).
 */
function WriteErrorNotice({ message, onDismiss }: { message: string; onDismiss: () => void }) {
    return (
        <div
            role="alert"
            className="mb-[14px] flex flex-wrap items-start justify-between gap-3 rounded-[9px] border border-[var(--danger-border)] bg-[var(--danger-tint)] px-3 py-[11px]"
        >
            <div className="min-w-0 flex-1">
                <p className="m-0 text-[13px] leading-[1.4] font-medium text-[var(--danger)]">
                    {surveyErrors.title}
                </p>
                <p className="m-0 mt-1 text-[13px] leading-[1.5] text-[var(--text-primary)]">
                    {message} {surveyErrors.keep}
                </p>
            </div>
            <Button variant="outline" size="sm" onClick={onDismiss}>
                {surveyErrors.dismiss}
            </Button>
        </div>
    );
}
